import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Icon, Input, Spinner, Switch } from '@components';
import { STORAGE_KEYS } from '@constants/storage';
import useMetaProvider from '@features/lead-management/hooks/useMetaProvider';
import useMetaSyncPolling from '@features/lead-management/hooks/useMetaSyncPolling';
import { addMetaSync } from '@features/lead-management/metaSyncSlice';
import { getMetaProviderErrorMessage } from '@features/lead-management/utils/metaIntegrationHelpers';
import useNotify from '@hooks/notify';
import { getLocalStorageItem } from '@utils/storage';

import MetaAccessTokenBanner from './MetaAccessTokenBanner';
import SaveMetaIntegrationModal from './SaveMetaIntegrationModal';

const MetaIntegration = () => {
  const dispatch = useDispatch();
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { createSuccessAlert, createErrorAlert } = useNotify();

  const {
    providerData,
    isLoading,
    hasProvider,
    createProvider,
    updateProvider,
    isCreating,
    isUpdating
  } = useMetaProvider(organizationId);

  const { pollMetaSyncStatus } = useMetaSyncPolling();

  const [accessToken, setAccessToken] = useState(
    providerData?.access_token ?? ''
  );
  const [isActive, setIsActive] = useState(providerData?.is_active ?? true);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isEditingToken, setIsEditingToken] = useState(false);
  const [updateSource, setUpdateSource] = useState<'modal' | 'toggle' | null>(
    null
  );

  const tokenInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!providerData) return;
    setIsActive(providerData.is_active);
    if (!isEditingToken) {
      setAccessToken(providerData.access_token ?? '');
      setIsEditingToken(false);
    }
  }, [providerData, isEditingToken]);

  useEffect(() => {
    if (isEditingToken) {
      tokenInputRef.current?.focus();
    }
  }, [isEditingToken]);

  const handleReset = () => {
    setAccessToken(hasProvider ? providerData?.access_token ?? '' : '');
    setIsEditingToken(false);
  };

  const handleToggleChange = async (value: boolean) => {
    setIsActive(value);
    if (!hasProvider) return;

    setUpdateSource('toggle');
    try {
      await updateProvider({
        organizationId,
        providerId: providerData!.id,
        body: { is_active: value }
      }).unwrap();

      createSuccessAlert(
        `Lead syncing ${value ? 'enabled' : 'paused'} successfully!`
      );
    } catch {
      createErrorAlert('Failed to update lead syncing status.');
    } finally {
      setUpdateSource(null);
    }
  };

  const handleModalConfirm = async (date: Date) => {
    if (!accessToken.trim()) {
      createErrorAlert('Access token is required.');
      return;
    }
    if (!date) {
      createErrorAlert('Sync start date is required.');
      return;
    }

    if (hasProvider) {
      setUpdateSource('modal');
      try {
        const response = await updateProvider({
          organizationId,
          providerId: providerData!.id,
          body: {
            access_token: accessToken,
            initial_sync_date: date.toISOString()
          }
        }).unwrap();

        setIsEditingToken(false);
        setIsSaveModalOpen(false);

        if (response?.job_id) {
          const syncId = `meta-sync-${Date.now()}`;
          dispatch(addMetaSync({ id: syncId, jobId: response.job_id }));

          await pollMetaSyncStatus({
            syncId,
            jobId: response.job_id
          });
        }
      } catch (error) {
        createErrorAlert(getMetaProviderErrorMessage(error), {
          classOverride: 'max-w-xl'
        });
      } finally {
        setUpdateSource(null);
      }
      return;
    }

    try {
      const response = await createProvider({
        organizationId,
        body: {
          access_token: accessToken,
          is_active: isActive,
          initial_sync_date: date.toISOString()
        }
      }).unwrap();

      setIsSaveModalOpen(false);

      if (response?.job_id) {
        const syncId = `meta-sync-${Date.now()}`;
        dispatch(addMetaSync({ id: syncId, jobId: response.job_id }));

        await pollMetaSyncStatus({
          syncId,
          jobId: response.job_id
        });
      }
    } catch (error) {
      createErrorAlert(getMetaProviderErrorMessage(error), {
        classOverride: 'max-w-xl'
      });
    }
  };

  const handleEditTokenClick = () => {
    setIsEditingToken(true);
    setAccessToken('');
  };

  const handleTokenBlur = () => {
    if (isEditingToken && !accessToken.trim() && providerData?.access_token) {
      setAccessToken(providerData.access_token);
      setIsEditingToken(false);
    }
  };

  const maskedAccessToken =
    hasProvider && accessToken && !isEditingToken
      ? '•'.repeat(Math.min(12, Math.max(0, accessToken.length - 4))) +
        accessToken.slice(-4)
      : accessToken;

  const isTokenInputDisabled = hasProvider && !isEditingToken;

  const hasTokenChange = hasProvider
    ? accessToken !== (providerData?.access_token ?? '')
    : !!accessToken.trim();

  const isSaveButtonDisabled =
    !accessToken.trim() ||
    (hasProvider && !hasTokenChange) ||
    (updateSource === 'toggle' && isUpdating);

  if (isLoading) {
    return (
      <div className="flex min-h-70 flex-col items-center justify-center rounded-xl border border-gray-100 bg-white shadow-sm">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="w-full rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="space-y-6 p-6">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Switch
              value={isActive}
              handleClick={handleToggleChange}
              disabled={isUpdating}
              testId="meta-lead-sync"
            />
            <span className="text-base font-semibold text-greyText1">
              Enable lead syncing
            </span>
          </div>
          <p className="text-sm text-grey80">
            Pause or resume automatic ingestion of Meta leads.
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold tracking-[-0.2px] text-greyText1">
            Access token<span className="text-error"> *</span>
          </label>
          <div className="relative w-full max-w-[396px]">
            <Input
              ref={tokenInputRef}
              type="text"
              value={maskedAccessToken}
              handleInputChange={setAccessToken}
              handleInputBlur={handleTokenBlur}
              placeholder="Enter access token"
              disabled={isTokenInputDisabled}
              className="w-full max-w-[396px]"
              inputStyle="h-12.5 w-full rounded-lg border border-grey30 px-4 py-2.5 pr-10 text-base font-normal leading-none tracking-normal placeholder:text-cofeeNeutral6 focus:border-cofeePrimary6 focus:outline-none disabled:cursor-not-allowed disabled:bg-grey20 disabled:text-grey60"
            />
            {isTokenInputDisabled && (
              <Button
                variant="transparent"
                handleClick={handleEditTokenClick}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 transition-opacity hover:bg-grey20"
                aria-label="Edit access token"
              >
                <Icon
                  name="edit"
                  size="none"
                  className="size-4 text-grey"
                  stroke="theme.grey"
                />
              </Button>
            )}
          </div>
          <MetaAccessTokenBanner
            wrapperClassName="max-w-[396px]"
            bannerClassName="!px-0.5 hover:opacity-80"
          />
        </div>
      </div>

      <div className="flex min-h-17 items-center justify-end gap-3 px-6 pb-6">
        {(!hasProvider || isEditingToken) && (
          <>
            <Button
              variant="tertiary"
              handleClick={handleReset}
              disabled={isCreating}
              className="w-22.5 px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="w-22.5 px-4 py-2 text-white"
              handleClick={() => setIsSaveModalOpen(true)}
              disabled={isSaveButtonDisabled}
              loading={isCreating || (isUpdating && updateSource === 'modal')}
            >
              Save
            </Button>
          </>
        )}
      </div>

      <SaveMetaIntegrationModal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onConfirm={handleModalConfirm}
      />
    </div>
  );
};

export default MetaIntegration;
